// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.Mapping.jsx" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//  </copyright>
// ----------------------------------------------------------------------

/**
 * Mapping component
 */

import React, { Component } from 'react';
import PropTypes from 'prop-types';
import connect from 'share/enhancers/Izenda.Connect';
import constants from 'logic/data/Izenda.ImportDataModelConstants';
import Validator from 'share/components/hoc/Izenda.Validator';
import MappingHeader from './Izenda.MappingHeader';
import Panel from 'share/components/controls/Izenda.Panel';
import { Table, Column, ColumnGroup } from 'share/components/controls/DataGrid/Izenda.Table';
import _DropDown from 'share/components/controls/Izenda.DropDown';
import { isNil, find, get, getOr, toLower, flatMap, isEmpty, each, join } from 'lodash/fp';

const DropDown = Validator()(_DropDown);

@connect()
export default class Mapping extends Component {
  static propTypes = {
    store: PropTypes.object.isRequired
  };

  render() {
    const { localization, store : { dataModelImport : { selectedTenant } } } = this.props;
    const TABLE_DEF = constants.TABLE_DEF.TENANT_MAPPING;

    const addMapping = selectedTenant.addMapping.bind(selectedTenant);
    const header = {
      component: MappingHeader,
      addMapping,
      localization
    };

    return (
      <Panel header={header} className='izenda-CopyManagementMappings u_borderAll'>
        <Table dataSource={selectedTenant.mappings} minHeight={200} maxHeight={200}>
          <ColumnGroup title={localization.getText('COMMON_LABEL_FROM')}>
            <Column {...TABLE_DEF['fromDatabaseName']} cell={this.renderFromConnectionName} />
            <Column {...TABLE_DEF['fromObject']} cell={this.renderFromSchemaName} />
          </ColumnGroup>
          <ColumnGroup title={localization.getText('COMMON_LABEL_TO')}>
            <Column {...TABLE_DEF['toDatabaseName']} cell={this.renderToConnectionName} />
            <Column {...TABLE_DEF['toObject']} cell={this.renderToSchemaName} />
          </ColumnGroup>
          <ColumnGroup title='&nbsp;'>
            <Column {...TABLE_DEF['action']} cell={this.renderActionColumn} />
          </ColumnGroup>
        </Table>
      </Panel>
    );
  }

  renderFromConnectionName = (mappingItem: any, key: string, rowIndex: number) => {
    const { store : { dataModelImport : { selectedTenant } } } = this.props;
    const options = selectedTenant.sourceConnections;

    const changeDatabaseName = (value) => {
      mappingItem.fromConnectionName = value.value;
      mappingItem.fromSchemaName = '';
    };

    return (
      <DropDown
        options={options}
        onChange={changeDatabaseName}
        value={mappingItem.fromConnectionName}
        valueField='name'
        labelField='name'
        field={key + rowIndex}
        validations={constants.REQUIRE_CONSTRAINT}
        group={'mapping' + rowIndex}
      />
    );
  };

  renderToConnectionName = (mappingItem: any, key: string, rowIndex: number) => {
    const { store : { dataModelImport : { selectedTenant } } } = this.props;
    const options = selectedTenant.destinationConnections;

    const changeConnectionName = (value) => {
      mappingItem.toConnectionName = value.value;
      mappingItem.toSchemaName = '';
    };

    return (
      <DropDown
        options={options}
        onChange={changeConnectionName}
        value={mappingItem.toConnectionName}
        valueField='name'
        labelField='name'
        field={key + rowIndex}
        validations={constants.REQUIRE_CONSTRAINT}
        group={'mapping' + rowIndex}
      />
    );
  };

  renderFromSchemaName = (mappingItem: any, key: string, rowIndex: number) => {
    const { store : { dataModelImport : { selectedTenant } } } = this.props;

    const selectedConnection = find(c => c.name === mappingItem.fromConnectionName, selectedTenant.sourceConnections);
    const options = getOr([], 'dbSource.querySources', selectedConnection);

    const changeSchemaName = (value) => {
      mappingItem.fromSchemaName = value.value;
    };

    return (
      <DropDown
        options={options}
        onChange={changeSchemaName}
        value={mappingItem.fromSchemaName}
        valueField='name'
        labelField='name'
        field={key + rowIndex}
        validations={constants.REQUIRE_CONSTRAINT}
        group={'mapping' + rowIndex}
      />
    );
  };

  renderToSchemaName = (mappingItem: any, key: string, rowIndex: number) => {
    const { store : { dataModelImport : { selectedTenant } } } = this.props;

    const selectedConnection = find(c => c.name === mappingItem.toConnectionName, selectedTenant.destinationConnections);
    const options = getOr([], 'dbSource.querySources', selectedConnection);

    const changeSchemaName = (value) => {
      mappingItem.toSchemaName = value.value;
    };

    return (
      <DropDown
        options={options}
        onChange={changeSchemaName}
        value={mappingItem.toSchemaName}
        valueField='name'
        labelField='name'
        field={key + rowIndex}
        validations={constants.REQUIRE_CONSTRAINT}
        group={'mapping' + rowIndex}
      />
    );
  };

  /*
   * Render Action Column
   */
  renderActionColumn = (mappingItem: any) => {
    const { localization } = this.props;
    return (
      <a onClick={this.deleteMapping.bind(this, mappingItem)} className='action-column'>
        <span title={localization.getText('COMMON_LABEL_DELETE')} className='icon-cancel ' />
      </a>
    );
  };

    /*
   * Delete mapping item
   */
  deleteMapping = (mappingItem: any) => {
    const { store : { dataModelImport : { selectedTenant : { mappings } } } } = this.props;
    const deletedIndex = mappings.indexOf(mappingItem);
    const deleteCount = 1;
    mappings.splice(deletedIndex, deleteCount);
  };

}



// WEBPACK FOOTER //
// ./ui/modules/Settings/DataSetup/Import/DataModel/AdvancedImport/Mapping/Izenda.Mapping.jsx